// =====================================================
// 白板系统 - 项目列表页
// 卡片网格展示所有白板，支持新建/删除/复制/重命名/导入导出
// =====================================================

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Pencil,
  Trash2,
  Copy,
  MoreVertical,
  FileEdit,
  Clock,
  Download,
  Upload,
  AlertTriangle,
  Users,
  X,
} from 'lucide-react';
import { useWhiteboardStore } from '../stores/whiteboard-store';
import { useWhiteboardTabsStore } from '../stores/whiteboard-tabs-store';
import { ProjectDialog } from '../components/ProjectDialog';
import * as service from '../services/whiteboard-service';
import type { WhiteboardProject } from '../types';

// 确认弹窗组件
function ConfirmDialog({
  isOpen, title, message, confirmText = '确定', cancelText = '取消',
  variant = 'danger', isLoading = false, onConfirm, onCancel,
}: {
  isOpen: boolean; title: string; message: string;
  confirmText?: string; cancelText?: string;
  variant?: 'danger' | 'warning';
  isLoading?: boolean; onConfirm: () => void; onCancel: () => void;
}) {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-[340px] p-5 animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-start gap-3 mb-4">
          <div className={`p-2 rounded-xl shrink-0 ${variant === 'danger' ? 'bg-red-50' : 'bg-amber-50'}`}>
            <AlertTriangle className={`w-5 h-5 ${variant === 'danger' ? 'text-red-500' : 'text-amber-500'}`} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
            <p className="text-xs text-gray-500 mt-1 leading-relaxed">{message}</p>
          </div>
        </div>
        <div className="flex gap-2 justify-end">
          <button onClick={onCancel} disabled={isLoading} className="px-4 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">{cancelText}</button>
          <button onClick={onConfirm} disabled={isLoading} className={`px-4 py-1.5 text-xs font-medium text-white rounded-lg transition-colors ${variant === 'danger' ? 'bg-red-500 hover:bg-red-600' : 'bg-amber-500 hover:bg-amber-600'
            } disabled:opacity-50`}>{isLoading ? '处理中...' : confirmText}</button>
        </div>
      </div>
    </div>
  );
}

export function WhiteboardListPage() {
  const navigate = useNavigate();
  const {
    projects,
    collabProjects,
    isLoadingProjects,
    loadProjects,
    createProject,
    deleteProject,
    duplicateProject,
    updateProjectMeta,
  } = useWhiteboardStore();
  const { openTab, closeTab, updateTabName } = useWhiteboardTabsStore();

  const [contextMenu, setContextMenu] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 弹窗状态
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'rename'>('create');
  const [dialogLoading, setDialogLoading] = useState(false);
  const [editingProject, setEditingProject] = useState<WhiteboardProject | null>(null);

  // 删除确认弹窗
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<WhiteboardProject | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  // 加载缩略图（从 Storage 下载，用户鉴权）
  const [thumbnails, setThumbnails] = useState<Record<string, string>>({});
  useEffect(() => {
    const allProjects = [...projects, ...collabProjects];
    if (!allProjects.length) return;
    const loadAll = async () => {
      const results: Record<string, string> = {};
      await Promise.all(
        allProjects.map(async (p) => {
          if (!p.storage_key) return;
          const url = await service.loadThumbnail(p.storage_key);
          if (url) results[p.id] = url;
        })
      );
      setThumbnails(results);
    };
    loadAll();
    // cleanup object URLs
    return () => {
      Object.values(thumbnails).forEach((url) => URL.revokeObjectURL(url));
    };
  }, [projects, collabProjects]);

  // 点击外部关闭菜单
  useEffect(() => {
    const handler = () => setContextMenu(null);
    window.addEventListener('click', handler);
    return () => window.removeEventListener('click', handler);
  }, []);

  // ===== 新建白板 =====
  const handleOpenCreate = () => {
    setDialogMode('create');
    setEditingProject(null);
    setDialogOpen(true);
    setContextMenu(null);
  };

  const handleCreateConfirm = async (name: string, description: string) => {
    setDialogLoading(true);
    try {
      const project = await createProject({ name, description });
      setDialogOpen(false);
      openTab(project.id, project.name);
      navigate(`/whiteboard/${project.id}`);
    } finally {
      setDialogLoading(false);
    }
  };

  // ===== 重命名 =====
  const handleOpenRename = (e: React.MouseEvent, project: WhiteboardProject) => {
    e.stopPropagation();
    setDialogMode('rename');
    setEditingProject(project);
    setDialogOpen(true);
    setContextMenu(null);
  };

  const handleRenameConfirm = async (name: string, description: string) => {
    if (!editingProject) return;
    setDialogLoading(true);
    try {
      await updateProjectMeta(editingProject.id, { name, description });
      updateTabName(editingProject.id, name);
      setDialogOpen(false);
    } finally {
      setDialogLoading(false);
    }
  };

  // ===== 删除 =====
  const handleDeleteClick = (e: React.MouseEvent, project: WhiteboardProject) => {
    e.stopPropagation();
    setDeleteTarget(project); setDeleteDialogOpen(true); setContextMenu(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await deleteProject(deleteTarget.id);
      closeTab(deleteTarget.id);
    } finally {
      setDeleteLoading(false); setDeleteDialogOpen(false); setDeleteTarget(null);
    }
  };

  // ===== 复制 =====
  const handleDuplicate = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    await duplicateProject(id);
    setContextMenu(null);
  };

  // ===== 导出 =====
  const handleExport = async (e: React.MouseEvent, project: WhiteboardProject) => {
    e.stopPropagation();
    setContextMenu(null);
    try {
      const canvasData = service.loadElements(project);
      const files = project.storage_key
        ? await service.loadFiles(project.storage_key, canvasData.elements || [])
        : {};

      const exportData = {
        type: 'excalidraw',
        version: 2,
        elements: canvasData.elements || [],
        appState: canvasData.appState || {},
        files,
      };

      const json = JSON.stringify(exportData, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${project.name}.excalidraw`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export error:', err);
      alert('导出失败，请重试');
    }
  };

  // ===== 导入 =====
  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const data = JSON.parse(text);

      // 从文件名提取名称
      const fileName = file.name.replace(/\.excalidraw$/, '').replace(/\.json$/, '');
      const project = await createProject({ name: fileName || '导入的白板' });

      // 写入画布数据到 DB
      await service.saveElements(project.id, {
        elements: data.elements || [],
        appState: data.appState || {},
      });
      navigate(`/whiteboard/${project.id}`);
    } catch (err) {
      console.error('Import error:', err);
    }

    // 清空 input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleOpen = (id: string) => {
    const allProjects = [...projects, ...collabProjects];
    const project = allProjects.find((p) => p.id === id);
    openTab(id, project?.name || '未命名白板');
    navigate(`/whiteboard/${id}`);
  };

  const handleUnpinCollab = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    await service.setCollaborativeProjectPinned(id, false);
    await loadProjects();
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('zh-CN', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <div className="flex-1 p-6 overflow-y-auto">
      {/* 隐藏的文件选择器 */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".excalidraw,.json"
        onChange={handleFileImport}
        className="hidden"
      />

      <ProjectDialog
        isOpen={dialogOpen}
        mode={dialogMode}
        initialName={editingProject?.name || ''}
        initialDescription={editingProject?.description || ''}
        onConfirm={dialogMode === 'create' ? handleCreateConfirm : handleRenameConfirm}
        onCancel={() => setDialogOpen(false)}
        isLoading={dialogLoading}
      />
      <ConfirmDialog
        isOpen={deleteDialogOpen}
        title="删除白板"
        message={`确定要删除「${deleteTarget?.name || ''}」吗？删除后将无法恢复。`}
        confirmText="删除" variant="danger"
        isLoading={deleteLoading}
        onConfirm={handleDeleteConfirm}
        onCancel={() => { setDeleteDialogOpen(false); setDeleteTarget(null); }}
      />

      <div className="max-w-6xl mx-auto">
        {/* 头部 */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Pencil className="w-6 h-6 text-blue-600" />
              我的白板
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              使用 Excalidraw 创建手绘风格的图表和笔记
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleImportClick}
              className="flex items-center gap-2 px-3 py-2 text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm"
            >
              <Upload className="w-4 h-4" />
              导入
            </button>
            <button
              onClick={handleOpenCreate}
              className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium shadow-sm"
            >
              <Plus className="w-4 h-4" />
              新建白板
            </button>
          </div>
        </div>

        {/* 内容区 */}
        {isLoadingProjects ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="w-10 h-10 border-3 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-sm text-gray-500">加载中...</p>
            </div>
          </div>
        ) : projects.length === 0 && collabProjects.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-20 h-20 bg-blue-50 rounded-2xl flex items-center justify-center mb-4">
              <Pencil className="w-10 h-10 text-blue-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-700 mb-2">还没有白板</h3>
            <p className="text-sm text-gray-400 mb-6">创建你的第一个白板，或导入已有的 Excalidraw 文件</p>
            <div className="flex gap-3">
              <button
                onClick={handleImportClick}
                className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors text-sm"
              >
                <Upload className="w-4 h-4" />
                导入文件
              </button>
              <button
                onClick={handleOpenCreate}
                className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                <Plus className="w-4 h-4" />
                创建白板
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {/* 新建卡片 */}
            <button
              onClick={handleOpenCreate}
              className="group flex flex-col items-center justify-center h-48 border-2 border-dashed border-gray-200 rounded-xl hover:border-blue-400 hover:bg-blue-50/50 transition-all"
            >
              <div className="w-12 h-12 rounded-xl bg-gray-100 group-hover:bg-blue-100 flex items-center justify-center transition-colors mb-2">
                <Plus className="w-6 h-6 text-gray-400 group-hover:text-blue-500 transition-colors" />
              </div>
              <span className="text-sm text-gray-400 group-hover:text-blue-600 font-medium transition-colors">
                新建白板
              </span>
            </button>

            {/* 项目卡片 */}
            {projects.map((project) => (
              <div
                key={project.id}
                onClick={() => handleOpen(project.id)}
                className="group relative bg-white rounded-xl border border-gray-200 overflow-hidden cursor-pointer hover:shadow-md hover:border-blue-300 transition-all"
              >
                <div className="h-36 bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-2">
                  {thumbnails[project.id] ? (
                    <img src={thumbnails[project.id]} alt={project.name} className="max-w-full max-h-full object-contain" />
                  ) : (
                    <Pencil className="w-8 h-8 text-gray-300" />
                  )}
                </div>

                <div className="p-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <h3 className="text-sm font-medium text-gray-800 truncate min-w-0">{project.name}</h3>
                    {project.share_mode === 'edit' && (
                      <span
                        className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-700"
                        title="该白板已开启协作"
                      >
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                        协作中
                      </span>
                    )}
                  </div>
                  {project.description && (
                    <p className="text-[11px] text-gray-400 truncate mt-0.5">{project.description}</p>
                  )}
                  <div className="flex items-center gap-1 mt-1">
                    <Clock className="w-3 h-3 text-gray-400" />
                    <span className="text-[11px] text-gray-400">{formatDate(project.updated_at)}</span>
                  </div>
                </div>

                {/* 更多菜单按钮 */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setContextMenu(contextMenu === project.id ? null : project.id);
                  }}
                  className="absolute top-2 right-2 p-1.5 rounded-lg bg-white/80 backdrop-blur-sm opacity-0 group-hover:opacity-100 hover:bg-gray-100 transition-all shadow-sm"
                >
                  <MoreVertical className="w-4 h-4 text-gray-500" />
                </button>

                {/* 下拉菜单 */}
                {contextMenu === project.id && (
                  <div
                    className="absolute top-10 right-2 z-50 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[130px]"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      onClick={(e) => handleOpenRename(e, project)}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
                    >
                      <FileEdit className="w-3.5 h-3.5" />
                      重命名
                    </button>
                    <button
                      onClick={(e) => handleDuplicate(e, project.id)}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
                    >
                      <Copy className="w-3.5 h-3.5" />
                      复制
                    </button>
                    <button
                      onClick={(e) => handleExport(e, project)}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
                    >
                      <Download className="w-3.5 h-3.5" />
                      导出
                    </button>
                    <hr className="my-1 border-gray-100" />
                    <button
                      onClick={(e) => handleDeleteClick(e, project)}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      删除
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {!isLoadingProjects && collabProjects.length > 0 && (
          <section className="mt-10">
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-5 h-5 text-emerald-600" />
              <div>
                <h2 className="text-lg font-semibold text-gray-900">与我协作</h2>
                <p className="text-xs text-gray-500">你手动添加的协作白板</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {collabProjects.map((project) => (
                <div
                  key={project.id}
                  onClick={() => handleOpen(project.id)}
                  className="group relative bg-white rounded-xl border border-emerald-200 overflow-hidden cursor-pointer hover:shadow-md hover:border-emerald-400 transition-all"
                >
                  <div className="h-36 bg-gradient-to-br from-emerald-50/60 to-gray-100 flex items-center justify-center p-2">
                    {thumbnails[project.id] ? (
                      <img src={thumbnails[project.id]} alt={project.name} className="max-w-full max-h-full object-contain" />
                    ) : (
                      <Users className="w-8 h-8 text-emerald-300" />
                    )}
                  </div>
                  <div className="p-3">
                    <h3 className="text-sm font-medium text-gray-800 truncate">{project.name}</h3>
                    <div className="flex items-center gap-1 mt-1">
                      <Clock className="w-3 h-3 text-gray-400" />
                      <span className="text-[11px] text-gray-400">{formatDate(project.updated_at)}</span>
                    </div>
                  </div>
                  <button
                    onClick={(e) => void handleUnpinCollab(e, project.id)}
                    className="absolute top-2 right-2 p-1.5 rounded-lg bg-white/90 text-gray-500 opacity-0 group-hover:opacity-100 hover:bg-red-50 hover:text-red-500 transition-all shadow-sm"
                    title="从与我协作中移除"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
